import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Logo, LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Wallet, MessageSquare, LogOut, Menu, ChevronRight, Sun, Moon, ArrowLeftRight,
} from "lucide-react";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/statements", label: "Statements", icon: Wallet },
  { to: "/portal/funds", label: "Funds", icon: ArrowLeftRight },
  { to: "/portal/messages", label: "Messages", icon: MessageSquare },
];

const NavItems = ({ onNavigate }) => (
  <nav className="flex-1 px-3 space-y-1">
    {NAV.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
        data-testid={`nav-${n.label.toLowerCase().split(" ")[0]}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-brand/10 text-foreground ring-1 ring-brand/20 font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
        <n.icon className="size-4" /> {n.label}
      </NavLink>
    ))}
  </nav>
);

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[280px] shrink-0 border-r glass flex-col h-dvh sticky top-0">
        <div className="h-16 flex items-center px-5 border-b"><Logo /></div>
        <div className="flex-1 py-4 overflow-y-auto"><NavItems /></div>
        <div className="p-3 border-t">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="size-9"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-brand/10 text-brand text-xs">{initials}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.account_no}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="h-16 border-b glass sticky top-0 z-40 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button data-testid="portal-mobile-menu-button" variant="ghost" size="icon" className="lg:hidden"><Menu className="size-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="portal-mobile-nav-sheet" className="p-0 w-[280px]">
                <div className="h-16 flex items-center px-5 border-b"><Logo /></div>
                <div className="py-4"><NavItems onNavigate={() => setMobileOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <LogoMark className="lg:hidden size-8" />
            <Badge data-testid="simulated-badge" variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 text-[11px]">
              <span className="size-1.5 rounded-full bg-amber-500 mr-1.5" /> SIMULATED DATA
            </Badge>
          </div>
          <div className="flex items-center gap-1">
          <button data-testid="theme-toggle-button" onClick={toggle} title="Toggle dark mode" className="size-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground">
            {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-full pr-2 hover:bg-muted transition-colors p-1">
                <Avatar className="size-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-brand/10 text-brand text-xs">{initials}</AvatarFallback></Avatar>
                <span className="hidden sm:block text-sm font-medium">{user?.name?.split(" ")[0]}</span>
                <ChevronRight className="size-4 text-muted-foreground rotate-90" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={doLogout} className="text-destructive focus:text-destructive"><LogOut className="size-4 mr-2" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
